import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Flex,
  Heading,
  Select,
  Text,
  TextField,
  type SelectOption,
} from '@chatsdet/ui';

type AzureDevopsDefaultSettingsCardProps = {
  brandName: string;
  projectOptions: SelectOption[];
  workItemTypeOptions: SelectOption[];
  selectedProjectId: string | null;
  selectedWorkItemType: string | null;
  areaPath: string;
  isLoadingProjects: boolean;
  isLoadingWorkItemTypes: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onProjectChange: (projectId: string | null) => void;
  onWorkItemTypeChange: (workItemType: string | null) => void;
  onAreaPathChange: (areaPath: string) => void;
  onSave: () => void;
};

export function AzureDevopsDefaultSettingsCard({
  brandName,
  projectOptions,
  workItemTypeOptions,
  selectedProjectId,
  selectedWorkItemType,
  areaPath,
  isLoadingProjects,
  isLoadingWorkItemTypes,
  isDirty,
  isSaving,
  onProjectChange,
  onWorkItemTypeChange,
  onAreaPathChange,
  onSave,
}: AzureDevopsDefaultSettingsCardProps) {
  return (
    <Card padding="lg">
      <CardHeader>
        <Heading level={3}>Default Settings</Heading>
        <Text size="sm" className="text-muted-foreground mt-1">
          Configure defaults for Azure DevOps work items created from {brandName} issues. A default
          project is required for status-sync webhooks.
        </Text>
      </CardHeader>
      <CardContent>
        <Flex direction="column" gap="lg">
          <div>
            <Text size="sm" className="font-medium mb-1.5">
              Default Project
            </Text>
            {isLoadingProjects ? (
              <Text size="sm" className="text-muted-foreground">
                Loading projects...
              </Text>
            ) : (
              <Select
                aria-label="Default Azure DevOps project"
                selectedKey={selectedProjectId}
                items={projectOptions}
                placeholder="Select a project..."
                onSelectionChange={(key) => onProjectChange((key as string) ?? null)}
              />
            )}
          </div>

          {selectedProjectId && (
            <div>
              <Text size="sm" className="font-medium mb-1.5">
                Default Work Item Type
              </Text>
              {isLoadingWorkItemTypes ? (
                <Text size="sm" className="text-muted-foreground">
                  Loading work item types...
                </Text>
              ) : (
                <Select
                  aria-label="Default work item type"
                  selectedKey={selectedWorkItemType}
                  items={workItemTypeOptions}
                  placeholder="Select a work item type..."
                  onSelectionChange={(key) => onWorkItemTypeChange((key as string) ?? null)}
                />
              )}
            </div>
          )}

          <div>
            <Text size="sm" className="font-medium mb-1.5">
              Default Area Path (optional)
            </Text>
            <TextField
              aria-label="Default area path"
              value={areaPath}
              placeholder="e.g. MyProject\\Team"
              onChange={onAreaPathChange}
            />
          </div>

          <Flex gap="sm">
            <Button variant="primary" size="sm" onPress={onSave} isDisabled={!isDirty || isSaving}>
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
}
